import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NpcPortrait, { npcLook, npcArtPortrait, npcSceneUrl, NPC_FX, NPC_HALO } from './NpcPortrait';
import NpcFx from './npcFx';
import { getNpcLines, pick } from './npcLines';
import StoryNpcTasks from '../StoryNpcTasks';
import AuthLayeredCard from '../AuthLayeredCard';
import { authButtonStyle } from '../../constants/authStyles';
import { playArtiBasSound } from '../../utils/buttonClickSound';
import { tl } from '../../utils/inlineI18n';
import './NpcDialogue.css';
import { translateGameName } from '../../utils/gameNameI18n';

const INTRO_MS = 3000;
const TEXT_AT_MS = 1950;
const TYPE_MS = 22;

/**
 * NPC'ye tıklanınca açılan sinematik diyalog ekranı.
 * İlk 3 saniye giriş efekti oynar, sonra NPC konuşur ve seçenekler gelir.
 */
const NpcDialogue = ({ npc, hasTask, canTrain = false, onTrain, villageId, username, onService, onClose, onCharacterChange }) => {
  const { i18n } = useTranslation();
  const look = useMemo(() => npcLook(npc), [npc]);
  const art = useMemo(() => npcArtPortrait(npc), [npc]);
  const L = useMemo(() => getNpcLines(look.type, i18n.language), [look.type, i18n.language]);
  const fxKind = NPC_FX[look.type];
  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const [intro, setIntro] = useState(!reduced);
  const [leaving, setLeaving] = useState(false);
  const [view, setView] = useState('talk');
  const [script, setScript] = useState(null); // { lines: string[], idx: number }
  const [shown, setShown] = useState('');
  const [typing, setTyping] = useState(false);

  const canvasRef = useRef(null);
  const pcolRef = useRef(null);
  const fxRef = useRef(null);
  const timers = useRef([]);
  const typeTimer = useRef(null);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const say = useCallback((lines) => {
    const arr = (Array.isArray(lines) ? lines : [lines]).filter(Boolean);
    setScript({ lines: arr, idx: 0 });
  }, []);

  // Daktilo efekti
  const current = script ? script.lines[script.idx] : '';
  useEffect(() => {
    clearInterval(typeTimer.current);
    if (!current) {
      setShown('');
      setTyping(false);
      return undefined;
    }
    if (reduced) {
      setShown(current);
      setTyping(false);
      return undefined;
    }
    let i = 0;
    setShown('');
    setTyping(true);
    typeTimer.current = setInterval(() => {
      i += 1;
      setShown(current.slice(0, i));
      if (i >= current.length) {
        clearInterval(typeTimer.current);
        setTyping(false);
      }
    }, TYPE_MS);
    return () => clearInterval(typeTimer.current);
  }, [current, script, reduced]);

  const finishTyping = () => {
    clearInterval(typeTimer.current);
    setShown(current);
    setTyping(false);
  };

  const hasMore = script && script.idx < script.lines.length - 1;
  const optionsVisible = !intro && script && !typing && !hasMore && view === 'talk';

  // Giriş koreografisi + parçacıklar
  useEffect(() => {
    const fx = new NpcFx(canvasRef.current);
    fxRef.current = fx;
    fx.start(fxKind);
    const greet = pick(L.greet);
    if (reduced) {
      say(greet);
    } else {
      const burstKind = fxKind === 'ember' ? 'spark' : fxKind === 'spark' ? 'spark' : fxKind === 'gold' ? 'gold' : 'dust';
      later(() => fx.burst(0.5, 0.88, burstKind === 'spark' ? 90 : 55, burstKind), 1250);
      if (look.type === 'demirci') {
        later(() => {
          if (art) fx.burst(0.42, 0.6, 70, 'spark'); else fx.burst(0.74, 0.4, 60, 'spark');
          try { playArtiBasSound(0.35); } catch (e) { /* ses opsiyonel */ }
        }, 1300);
      }
      later(() => say(greet), TEXT_AT_MS);
      later(() => setIntro(false), INTRO_MS);
    }
    const onResize = () => fx.size();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimers();
      clearInterval(typeTimer.current);
      window.removeEventListener('resize', onResize);
      fx.stop();
    };
    // NPC değişince sahne baştan kurulur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npc?.id]);

  const skipIntro = () => {
    if (!intro) return;
    clearTimers();
    setIntro(false);
    if (!script) say(pick(L.greet));
  };

  const close = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onClose?.(), 280);
  }, [leaving, onClose]);

  const goService = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onService?.(), 220);
  }, [leaving, onService]);

  const options = useMemo(() => {
    const list = [{ key: 'main', t: L.main, main: true, run: goService }];
    if (hasTask) list.push({ key: 'task', t: L.common.task, tag: '!', run: () => setView('tasks') });
    if (canTrain) {
      list.push({
        key: 'train',
        t: tl('Beni eğitir misin?', 'Can you train me?'),
        run: () => {
          if (leaving) return;
          setLeaving(true);
          setTimeout(() => onTrain?.(), 220);
        },
      });
    }
    list.push({ key: 'talk', t: L.common.talk, run: () => say(L.talk) });
    list.push({ key: 'bye', t: L.common.bye, run: close });
    return list;
  }, [L, hasTask, canTrain, onTrain, leaving, goService, close, say]);

  const onBoxClick = (e) => {
    if (e.target.closest('button')) return;
    if (typing) { finishTyping(); return; }
    if (hasMore) setScript((s) => ({ ...s, idx: s.idx + 1 }));
  };

  const backToTalk = () => {
    setView('talk');
    say(pick(L.common.afterBack));
  };

  // Klavye: Esc kapat/geri, Space/Enter ilerlet, 1-4 seçenek
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (view === 'tasks') backToTalk(); else close();
        return;
      }
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === ' ' || e.key === 'Enter') {
        if (intro) { e.preventDefault(); skipIntro(); return; }
        if (typing) { e.preventDefault(); finishTyping(); return; }
        if (hasMore) { e.preventDefault(); setScript((s) => ({ ...s, idx: s.idx + 1 })); }
        return;
      }
      if (optionsVisible && /^[1-9]$/.test(e.key)) {
        const o = options[Number(e.key) - 1];
        if (o) o.run();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const scene = npcSceneUrl(npc);
  const bg = scene ? `url("${scene}")` : 'none';

  return (
    <div
      className={`npcd npcd--${look.type} npcd--fx-${fxKind}${intro ? ' is-intro' : ''}${leaving ? ' is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={npc?.name}
      style={{ '--npcd-halo': NPC_HALO[look.type], '--npcd-bg': bg }}
      onClick={intro ? skipIntro : undefined}
    >
      <div className="npcd-backdrop" aria-hidden="true" />
      <div className="npcd-veil" aria-hidden="true" />
      <div className="npcd-bar npcd-bar--t" aria-hidden="true" />
      <div className="npcd-bar npcd-bar--b" aria-hidden="true" />

      <div className="npcd-stage">
        <div className="npcd-pcol" ref={pcolRef}>
          <div className="npcd-halo" aria-hidden="true" />
          <div className="npcd-portrait">
            {art ? (
              <div
                className={`npcd-art npcd-art--${art.light}${art.mirror ? ' is-mirror' : ''}`}
                style={{ '--npcd-art': `url("${art.src}")`, ...(art.tone ? { '--npcd-tone': art.tone } : {}) }}
              >
                <img src={art.src} alt={npc?.name || ''} draggable="false" />
                <div className="npcd-art-light" aria-hidden="true" />
              </div>
            ) : (
              <NpcPortrait npc={npc} />
            )}
          </div>
          <div className="npcd-sweep" aria-hidden="true" />
          <canvas className="npcd-fx" ref={canvasRef} aria-hidden="true" />
          <div className="npcd-nameplate">
            <div className="npcd-name">{translateGameName(npc?.name)}</div>
            {npc?.description ? <div className="npcd-role">{npc.description}</div> : null}
          </div>
        </div>

        <AuthLayeredCard className="npcd-box" onClick={onBoxClick}>
          <button type="button" className="npcd-close" onClick={close} aria-label={L.common.bye}>✕</button>
          <div className="npcd-speaker">{translateGameName(npc?.name)}</div>

          {view === 'talk' ? (
            <>
              <p className="npcd-line" aria-live="polite">
                {shown}
                {!typing && hasMore ? <span className="npcd-cont">▸ {L.common.continue}</span> : null}
              </p>
              <div className="npcd-opts">
                {optionsVisible
                  ? options.map((o, i) => (
                      <button
                        key={o.key}
                        type="button"
                        className={`auth-button npcd-opt${o.main ? ' is-main' : ''}`}
                        style={{ ...authButtonStyle, animationDelay: `${i * 70}ms` }}
                        onClick={o.run}
                      >
                        <span className="npcd-key">{i + 1}</span>
                        <span className="npcd-opt-text">{o.t}</span>
                        {o.tag ? <span className="npcd-tag">{o.tag}</span> : null}
                      </button>
                    ))
                  : null}
              </div>
            </>
          ) : (
            <div className="npcd-tasks">
              <p className="npcd-line">{L.common.taskIntro}</p>
              <StoryNpcTasks
                npcName={npc?.name}
                villageId={villageId}
                username={username}
                onCharacterChange={onCharacterChange}
                onSay={(lines) => { setView('talk'); say(lines); }}
              />
              <button
                type="button"
                className="auth-button npcd-opt"
                style={authButtonStyle}
                onClick={backToTalk}
              >
                <span className="npcd-opt-text">← {L.common.back}</span>
              </button>
            </div>
          )}
          {intro ? <div className="npcd-skip">{L.common.skip}</div> : null}
        </AuthLayeredCard>
      </div>
    </div>
  );
};

/** Dükkan / demirci ekranının üstünde duran küçük NPC şeridi. */
export const NpcServiceStrip = ({ npc, onTalk }) => {
  const { i18n } = useTranslation();
  const look = useMemo(() => npcLook(npc), [npc]);
  const art = useMemo(() => npcArtPortrait(npc), [npc]);
  const L = useMemo(() => getNpcLines(look.type, i18n.language), [look.type, i18n.language]);
  const [line] = useState(() => pick(L.service));
  return (
    <div className={`npcd-strip npcd--${look.type}`}>
      <div className="npcd-strip-medal">
        {art ? (
          <div
            className={`npcd-strip-art${art.mirror ? ' is-mirror' : ''}`}
            style={{ backgroundImage: `url("${art.src}")`, backgroundPosition: art.thumb, ...(art.tone ? { filter: art.tone } : {}) }}
            role="img"
            aria-label={npc?.name || ''}
          />
        ) : (
          <NpcPortrait npc={npc} viewBox="78 70 144 144" />
        )}
      </div>
      <div className="npcd-strip-text">
        <span className="npcd-strip-name">{translateGameName(npc?.name)}</span>
        <span className="npcd-strip-line">“{line}”</span>
      </div>
      <button type="button" className="npcd-strip-btn" onClick={onTalk}>
        {L.common.backToTalk}
      </button>
    </div>
  );
};

export default NpcDialogue;
